import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

const SEEDS = [
  { email: "admin@entrancezone.com", label: "Admin (all roles)" },
  { email: "editor@entrancezone.com", label: "Editor" },
  { email: "researcher@entrancezone.com", label: "Researcher" },
];

const IS_PROD = window.location.hostname === "editorial.entrancezone.com";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@entrancezone.com");
  const [password, setPassword] = useState("Admin@123");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (ex) {
      setErr(apiErr(ex, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-slate-200 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-blue-400" />
          <span className="font-serif text-xl font-bold text-white">EntranceZone</span>
        </div>
        <div className="max-w-md">
          <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-4">Private AI Editorial Intelligence</p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-white">
            Find what changed. Verify it. Patch it safely.
          </h1>
          <p className="mt-5 text-slate-400 leading-relaxed">
            An evidence-first assistant for WordPress content — it never publishes on its own. Every edit is
            researched, diffed, hash-verified and approved by a human before going live.
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono">Phase 1 · Posts only · Dry Run default</div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#FBFBF9]">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-serif text-2xl font-bold text-slate-900">Sign in</h2>
            <span
              data-testid="login-env-badge"
              className={`rounded-full border px-2.5 py-0.5 text-[11px] font-mono font-semibold uppercase tracking-widest ${
                IS_PROD ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"
              }`}
            >
              {IS_PROD ? "Production" : "Preview"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            {IS_PROD
              ? "Internal access only — live data, scheduled jobs running."
              : "Development panel — separate database, background jobs disabled."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-slate-500">Email</label>
              <input
                data-testid="login-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-slate-500">Password</label>
              <input
                data-testid="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            {err && <div data-testid="login-error" className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{err}</div>}
            <button
              data-testid="login-submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </button>
          </form>
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-mono uppercase tracking-wide text-slate-400 mb-2">Seeded accounts</div>
            {SEEDS.map((s) => (
              <button
                key={s.email}
                onClick={() => { setEmail(s.email); setPassword(s.email.startsWith("admin") ? "Admin@123" : s.email.startsWith("editor") ? "Editor@123" : "Research@123"); }}
                className="block w-full text-left text-xs text-slate-600 hover:text-blue-600 py-0.5"
              >
                {s.label} — <span className="font-mono">{s.email}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
